import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* any functionality that's shared between component variants but not needed by external components goes in this file */
import React, { useState, useRef, useEffect, useReducer } from 'react';
import { clickBaseEvent } from '@chegg-me-components/utils';
import { rioClient } from './rio';
export var defaultContext = {
  envUrl: '/',
  dataTestPrefix: 'signed-out-header'
};
/** A react context object that holds globally shareable properties amongst all the sub-components */

export var HeaderContext = /*#__PURE__*/React.createContext(defaultContext);
/** =====================================================================
 * Custom hooks for controlling behaviour of different header components
 * ===================================================================== */

/** This is a custom react hook used for maintaining actions, elements and state for hamburger menu */

export var useHamburgerMenu = function useHamburgerMenu(_ref) {
  var onCloseHamburger = _ref.onCloseHamburger,
      onOpenHamburger = _ref.onOpenHamburger;

  /** `isOpen` maintains the visual state of hamburger menu */
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];
  /** `launcher` is react ref for the element that is opening the hamburger menu */


  var launcher = useRef(null);
  /** `aborter` is react ref for the element that is closing the hamburger menu */

  var aborter = useRef(null);
  var allTimeouts = []; // array for storing all the timeouts so that all of them can be cleaned up when component unmounts

  /** `openHamburgerMenu` is the function used for opening the hamburger menu */

  var openHamburgerMenu = function openHamburgerMenu(event) {
    // adding a 0sec setTimeout so that 2 separate synthetic events triggering same callback at the same time don't repeat state update
    allTimeouts.push(window.setTimeout(function () {
      /* istanbul ignore next */
      if (!isOpen) {
        var _aborter$current;

        setIsOpen(true);
        (_aborter$current = aborter.current) === null || _aborter$current === void 0 ? void 0 : _aborter$current.focus();
        if (onOpenHamburger) onOpenHamburger(event); // calling the `onOpenHamburger` callback passed externally
      }
    }, 0));
  };
  /** `openHamburgerMenu` is the function used for closing the hamburger menu */


  var closeHamburgerMenu = function closeHamburgerMenu(event) {
    // adding a 0sec setTimeout so that 2 separate synthetic events triggering same callback at the same time don't repeat state update
    allTimeouts.push(window.setTimeout(function () {
      if (isOpen) {
        var _launcher$current;

        setIsOpen(false);
        /* istanbul ignore next */

        (_launcher$current = launcher.current) === null || _launcher$current === void 0 ? void 0 : _launcher$current.focus();
        if (onCloseHamburger) onCloseHamburger(event); // calling the `onCloseHamburger` callback passed externally
      }
    }, 0));
  }; // useEffect hook for cleaning up all the created timeouts once the component unmounts


  useEffect(function () {
    return function () {
      allTimeouts.forEach(function (timeoutID) {
        return clearTimeout(timeoutID);
      });
    };
  }, []);
  return {
    isOpen: isOpen,
    openHamburgerMenu: openHamburgerMenu,
    closeHamburgerMenu: closeHamburgerMenu,
    launcher: launcher,
    aborter: aborter
  };
};
/** Custom react hook for desktop center nav which controls the active state of all its nav items */

/** This is required as we wanna keep only one nav item active at a moment */

export var useDesktopCenterNavBehaviour = function useDesktopCenterNavBehaviour() {
  /** `activeNavItem` state indicates the current active nav item */
  var _useState3 = useState(-1),
      _useState4 = _slicedToArray(_useState3, 2),
      activeNavItem = _useState4[0],
      setActiveNavItem = _useState4[1];
  /** `activeNavItemRef` is required to access the latest value of state `activeNavItem` inside setTimeout;
   *   mainly used for establishing hover intent of fly out menu
   */


  var activeNavItemRef = useRef(activeNavItem);
  /** `useEffect` to update `activeNavItemRef` with latest value of state `activeNavItem` */

  useEffect(function () {
    activeNavItemRef.current = activeNavItem;
  }, [activeNavItem]);
  /** `getActiveNavItem` gets the latest value of state `activeNavItem` */

  var getActiveNavItem = function getActiveNavItem() {
    return activeNavItemRef.current;
  };
  /** `flyOutHoverOutDelayTimer` is to keep track of delay timer kept for un-setting active state of fly out
   *  This timer must be shared across all nav items and preserved across re-renders
   */


  var flyOutHoverOutDelayTimer = useRef(0);
  return {
    activeNavItem: activeNavItem,
    getActiveNavItem: getActiveNavItem,
    setActiveNavItem: setActiveNavItem,
    flyOutHoverOutDelayTimer: flyOutHoverOutDelayTimer
  };
};
/** Custom react hook for controlling the behaviour of desktop fly out menu
 * @param {number} index Tells the unique index of the fly out menu
 * @param {boolean} isActive Indicates whether the fly out menu is active or not
 * @param {function} getActiveNavItem gets the current active nav item in the nav
 * @param {function} setActiveNavItem sets active state of nav item in the nav
 */

export var useFlyOutMenuBehaviour = function useFlyOutMenuBehaviour(index, isActive, getActiveNavItem, setActiveNavItem, flyOutHoverOutDelayTimer) {
  /** `flyOutMenuRef` is the ref for the fly out menu */
  var flyOutMenuRef = useRef(null);
  /** `triggerButtonRef` is the ref for trigger button or menu button which toggles the active state of fly out menu */

  var triggerButtonRef = useRef(null);
  /** close fly-out menu when user clicks outside of it */

  var onClickOutsideFlyOut = function onClickOutsideFlyOut(event) {
    var _flyOutMenuRef$curren;

    event.stopPropagation();

    if (!((_flyOutMenuRef$curren = flyOutMenuRef.current) !== null && _flyOutMenuRef$curren !== void 0 && _flyOutMenuRef$curren.contains(event.target))) {
      var _triggerButtonRef$cur;

      clearTimer();
      toggleActiveState();
      /** bring focus back to trigger button */

      (_triggerButtonRef$cur = triggerButtonRef.current) === null || _triggerButtonRef$cur === void 0 ? void 0 : _triggerButtonRef$cur.focus();
    }
  };
  /** `useEffect` to register on click outside fly out */


  useEffect(function () {
    if (isActive) window.addEventListener('click', onClickOutsideFlyOut);
    return function () {
      window.removeEventListener('click', onClickOutsideFlyOut);
    };
  }, [isActive]);
  /** `useEffect` to clean up fly-out-delay-timer*/

  useEffect(function () {
    return function () {
      clearTimer();
    };
  }, []);
  /** toggles the active state of fly out */

  var toggleActiveState = function toggleActiveState() {
    isActive ? setActiveNavItem(-1) : setActiveNavItem(index);
  };
  /** clears fly out timer */


  var clearTimer = function clearTimer() {
    clearTimeout(flyOutHoverOutDelayTimer.current);
  };
  /** sets active state of fly out */


  var setActiveState = function setActiveState() {
    // isActive? clearTimer(): setActiveNavItem(index);
    clearTimer();
    if (!isActive) setActiveNavItem(index);
  };
  /** un-sets active state of fly out */


  var unsetActiveState = function unsetActiveState(event, delayInMS) {
    event.stopPropagation();
    var timeoutDelayInMS = delayInMS !== null && delayInMS !== void 0 ? delayInMS : 1000;

    if (isActive) {
      flyOutHoverOutDelayTimer.current = window.setTimeout(function () {
        /** unset only when the current fly out was in set state */
        if (getActiveNavItem() === index) setActiveNavItem(-1);
      }, timeoutDelayInMS);
    }
  };

  return {
    flyOutMenuRef: flyOutMenuRef,
    triggerButtonRef: triggerButtonRef,
    toggleActiveState: toggleActiveState,
    setActiveState: setActiveState,
    unsetActiveState: unsetActiveState,
    clearTimer: clearTimer
  };
};
/** Custom react hook for getting the rect information of an HTMLElement */

export var useElementRectInfo = function useElementRectInfo(targetRef) {
  /** create a ref only if target ref is not provided */
  var elementRef = targetRef || useRef(null);
  /**
   * The state is maintained because the `elementRef` changes its state after getting applied to the element and the
   * state change in `elementRef` is getting captured in `useEffect` which further updates the `rectInfo` in this state
   */

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      rectInfo = _useState6[0],
      setRectInfo = _useState6[1];

  useEffect(function () {
    var _elementRef$current;

    /**
     * These wouldn't be a case where `listItemRef.current` would be undefined. It's typescript that assumes that's
     * covering the case of `listItemRef.current` being undefined by using `?` operator
     */

    /* istanbul ignore next */
    setRectInfo((_elementRef$current = elementRef.current) === null || _elementRef$current === void 0 ? void 0 : _elementRef$current.getBoundingClientRect());
  }, [elementRef]);
  return {
    elementRef: elementRef,
    rectInfo: rectInfo
  };
};
/** Custom react hook for controlling the behaviour of mobile accordion */

export var useAccordion = function useAccordion(navLinks, activeLink) {
  var initialState = {
    openIndex: undefined
  };
  navLinks.forEach(function (navItem, index) {
    /* istanbul ignore next */
    if (!isNavLinkItem(navItem) && (activeLink === null || activeLink === void 0 ? void 0 : activeLink.parent) === navItem.parent.text && !!(activeLink !== null && activeLink !== void 0 && activeLink.child)) {
      initialState.openIndex = index;
    }
  });

  var reducer = function reducer(state, action) {
    switch (action.type) {
      case 'update':
        {
          var _index = action.data.index;
          if (_index === state.openIndex) return {
            openIndex: undefined
          };
          return {
            openIndex: _index
          };
        }
    }
  };

  var _useReducer = useReducer(reducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  return {
    state: state,
    dispatch: dispatch
  };
};
/** =====================================================================
 * Utility functions
 * ===================================================================== */

/** Function to identify if a particular nav item is an instance of nav link item */

export var isNavLinkItem = function isNavLinkItem(navItem) {
  return 'href' in navItem;
};
/** Function to identify if an array is an instance of nav link items or nav menu items */

export var isNavLinkItemArray = function isNavLinkItemArray(navItemArray) {
  var navLinkArray = navItemArray.map(function (navItem) {
    return isNavLinkItem(navItem);
  }).filter(Boolean);
  return navLinkArray.length === navItemArray.length;
};
export var generateRioEventNavText = function generateRioEventNavText(navItems) {
  return navItems.map(function (navItem) {
    return isNavLinkItem(navItem) ? navItem.text : navItem.children ? generateRioEventNavText(navItem.children) : navItem.parent.text;
  }).join('|').toLowerCase();
};
export var onClickLogoRio = function onClickLogoRio(isInsideHamburger) {
  // Rio Start
  rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
    data: {
      interaction: {
        element: {
          name: 'nav homepage shortcut',
          type: 'link',
          region_name: isInsideHamburger ? 'side nav' : 'top nav',
          value: 'chegg',
          text: 'Chegg'
        },
        type: 'click'
      }
    }
  })); // Rio Finished
};